import React, { createContext, useContext, useState, useCallback } from 'react'

interface I18nContextType {
  currentLanguage: string
  setLanguage: (language: string) => void
  t: (key: string, variables?: Record<string, string | number>) => string
  updateUI: () => void
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

export const useI18nContext = () => {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error('useI18nContext must be used within an I18nProvider')
  }
  return context
}

// Translations object (migrated from original app)
const translations = {
  es: {
    'app-title': 'RememMed',
    'login': 'Iniciar Sesión',
    'register': 'Crear Cuenta',
    'username': 'Usuario',
    'password': 'Contraseña',
    'email': 'Correo Electrónico',
    'loading': 'Cargando...',
    'home': 'Inicio',
    'medications': 'Medicamentos',
    'calendar': 'Calendario',
    'progress': 'Progreso',
    'profile': 'Perfil',
    'users': 'Caso Actual',
    'no-medications': 'No hay medicamentos registrados',
    'add-medication': 'Agregar Medicamento',
    'take-medication': 'Tomar Medicamento',
    'edit': 'Editar',
    'delete': 'Eliminar',
    'save': 'Guardar',
    'cancel': 'Cancelar',
    'confirm': 'Confirmar',
    'logout': 'Cerrar Sesión',
    'settings': 'Configuración',
    'language': 'Idioma',
    'theme': 'Tema',
    'light': 'Claro',
    'dark': 'Oscuro',
    'notifications': 'Notificaciones',
    'reminders': 'Recordatorios',
    'general': 'General',
    'account': 'Cuenta',
    'privacy': 'Privacidad',
    'about': 'Acerca de',
    'help': 'Ayuda',
    'support': 'Soporte',
    'feedback': 'Comentarios',
    'version': 'Versión',
    'contact': 'Contacto',
    'terms': 'Términos',
    'policy': 'Política',
    'close': 'Cerrar',
    'open': 'Abrir',
    'search': 'Buscar',
    'filter': 'Filtrar',
    'sort': 'Ordenar',
    'refresh': 'Actualizar',
    'sync': 'Sincronizar',
    'backup': 'Respaldo',
    'restore': 'Restaurar',
    'export': 'Exportar',
    'import': 'Importar',
    'share': 'Compartir',
    'copy': 'Copiar',
    'paste': 'Pegar',
    'cut': 'Cortar',
    'undo': 'Deshacer',
    'redo': 'Rehacer',
    'select-all': 'Seleccionar todo',
    'deselect-all': 'Deseleccionar todo',
    'yes': 'Sí',
    'no': 'No',
    'ok': 'OK',
    'done': 'Listo',
    'finish': 'Finalizar',
    'continue': 'Continuar',
    'back': 'Atrás',
    'next': 'Siguiente',
    'previous': 'Anterior',
    'first': 'Primero',
    'last': 'Último',
    'today': 'Hoy',
    'yesterday': 'Ayer',
    'tomorrow': 'Mañana',
    'week': 'Semana',
    'month': 'Mes',
    'year': 'Año',
    'daily': 'Diario',
    'weekly': 'Semanal',
    'monthly': 'Mensual',
    'yearly': 'Anual',
    'name': 'Nombre',
    'description': 'Descripción',
    'date': 'Fecha',
    'time': 'Hora',
    'status': 'Estado',
    'active': 'Activo',
    'inactive': 'Inactivo',
    'enabled': 'Habilitado',
    'disabled': 'Deshabilitado',
    'required': 'Requerido',
    'optional': 'Opcional',
    'available': 'Disponible',
    'unavailable': 'No disponible',
    'online': 'En línea',
    'offline': 'Fuera de línea',
    'connected': 'Conectado',
    'disconnected': 'Desconectado',
    'success': 'Éxito',
    'error': 'Error',
    'warning': 'Advertencia',
    'info': 'Información',
    'emergency': 'Emergencia',
    'urgent': 'Urgente',
    'normal': 'Normal',
    'low': 'Bajo',
    'medium': 'Medio',
    'high': 'Alto',
    'very-high': 'Muy alto',
    'all': 'Todo',
    'none': 'Ninguno',
    'other': 'Otro',
    'unknown': 'Desconocido',
    'new': 'Nuevo',
    'recent': 'Reciente',
    'old': 'Antiguo',
    'favorite': 'Favorito',
    'bookmark': 'Marcador',
    'tag': 'Etiqueta',
    'category': 'Categoría',
    'type': 'Tipo',
    'size': 'Tamaño',
    'color': 'Color',
    'image': 'Imagen',
    'video': 'Video',
    'audio': 'Audio',
    'document': 'Documento',
    'file': 'Archivo',
    'folder': 'Carpeta',
    'link': 'Enlace',
    'url': 'URL',
    'website': 'Sitio web',
    'page': 'Página',
    'section': 'Sección',
    'item': 'Elemento',
    'list': 'Lista',
    'table': 'Tabla',
    'chart': 'Gráfico',
    'graph': 'Gráfica',
    'report': 'Reporte',
    'summary': 'Resumen',
    'detail': 'Detalle',
    'overview': 'Vista general',
    'dashboard': 'Panel',
    'menu': 'Menú',
    'toolbar': 'Barra de herramientas',
    'sidebar': 'Barra lateral',
    'header': 'Encabezado',
    'footer': 'Pie de página',
    'content': 'Contenido',
    'main': 'Principal',
    'secondary': 'Secundario',
    'primary': 'Primario',
    'default': 'Predeterminado',
    'custom': 'Personalizado',
    'auto': 'Automático',
    'manual': 'Manual',
    'personal-info': 'Información Personal',
    'user-since': 'Usuario desde',
    'last-access': 'Último acceso',
    'member-since': 'Miembro desde',
    'last-login': 'Último inicio de sesión',
    'change-avatar': 'Cambiar avatar',
    'customize-avatar': 'Personalizar Avatar',
    'account-actions': 'Acciones de Cuenta',
    'logout-description': 'Esto cerrará tu sesión actual y te redirigirá a la pantalla de inicio de sesión.',
    // Progress Screen translations
    'progress-overview': 'Resumen de Progreso',
    'adherence-statistics': 'Estadísticas de Adherencia',
    'weekly-progress': 'Progreso Semanal',
    'monthly-progress': 'Progreso Mensual',
    'activity-history': 'Historial de Actividades',
    'current-streak': 'Racha Actual',
    'best-streak': 'Mejor Racha',
    'perfect-days': 'Días Perfectos',
    'total-taken': 'Total Tomados',
    'average-adherence': 'Adherencia Promedio',
    'total-scheduled': 'Total Programados',
    'medications-taken': 'Medicamentos Tomados',
    'medications-per-day': 'Medicamentos por Día',
    'adherence-by-week': 'Adherencia por Semana',
    'no-activities': 'No hay actividades que mostrar',
    'no-medications-date': 'No se registraron medicamentos en esta fecha',
    'start-tracking': 'Comienza a tomar tus medicamentos para ver tu progreso aquí',
    'advanced-statistics': 'Estadísticas Avanzadas',
    'last-week': 'Última semana',
    'last-month': 'Último mes',
    'last-3-months': 'Últimos 3 meses',
    'best-streak-days': 'Mejor racha (días)',
    'current-streak-days': 'Racha actual',
    'daily-average': 'Promedio diario',
    'time-patterns': 'Patrones de Horario',
    'recent-trend': 'Tendencia Reciente (7 días)',
    'insights-data': 'Insights de tus Datos',
    'current-level': 'Nivel',
    'medication-master': 'Maestro de Medicamentos',
    'total-points': 'Puntos Totales',
    'next-level': 'Siguiente nivel',
    'general-tab': 'General',
    'challenges-tab': 'Desafíos',
    'analytics-tab': 'Análisis',
    'active-challenge': 'Desafío Activo',
    'detailed-weekly': 'Análisis Detallado Semanal',
    'performance-summary': 'Resumen de Rendimiento',
    'total-medications': 'Total de Medicamentos',
    'general-adherence': 'Adherencia General',
    'most-active-hour': 'Hora Más Activa',
    'filter-all': 'Todas',
    'filter-taken': 'Tomadas',
    'filter-missed': 'Perdidas',
    'filter-stock': 'Stock ajustado',
    'group-by-date': 'Agrupar por fecha',
    'list-view': 'Vista lista',
    'previous-month': 'Mes anterior',
    'next-month': 'Mes siguiente',
    'current-month': 'Ir al mes actual',
    'month-label': 'Mes',
    'year-label': 'Año',
    'filter-label': 'Filtro',
    'taken-status': 'Tomado',
    'missed-status': 'Perdido',
    'skipped-status': 'Omitido',
    'stock-adjusted-status': 'Stock ajustado',
    'scheduled-time': 'Programado',
    'unknown-medication': 'Medicamento desconocido',
    'trend-improving': 'Tu adherencia está mejorando con el tiempo',
    'trend-declining': 'Tu adherencia ha disminuido. ¡Mantente enfocado!',
    'trend-stable': 'Tu adherencia se mantiene estable',
    'perfect-week': 'Semana Perfecta',
    'perfect-week-desc': 'Alcanza 100% de adherencia esta semana',
    'daily-consistency': 'Consistencia Diaria',
    'daily-consistency-desc': 'No falles ningún medicamento hoy',
    'weekly-reward': 'Badge "Semana Perfecta" + 500 puntos',
    'daily-reward': '100 puntos de bonus',
    'days-remaining': 'días restantes',
    'until-midnight': 'Hasta medianoche',
    'reward-label': 'Recompensa',
    'progress-label': 'Progreso',
    'personalized-insights': 'Insights Personalizados',
    'tip-label': 'Tip',
    'tip-message': 'Una vez que registres medicamentos tomados, aparecerán aquí para ayudarte a seguir tu progreso',
    'no-case-selected': 'No hay caso seleccionado',
    'select-case-progress': 'Selecciona un caso para ver el progreso',
    'achievements': 'Logros',
    'history': 'Historial',
    'stats-achievements': 'Estadísticas y logros de',
    'general-statistics': 'Estadísticas Generales',
    'level-experience': 'Nivel y Experiencia',
    'recent-adherence': 'Adherencia Reciente',
    'adherence': 'Adherencia',
    'hot-streak': '¡Racha ardiente!',
    'consecutive-days': 'días consecutivos',
    'level-achieved': '¡Nivel',
    'true-expert': 'Eres un verdadero experto',
    'royal-adherence': 'Adherencia real!',
    'top-1-percent': 'Estás en el top 1%',
    'perfect-days-achieved': 'días perfectos conseguidos',
    'golden-hour': 'Tu hora dorada',
    // Medications Screen translations
    'manage-medications': 'Gestiona tus medicamentos y horarios',
    'add-medication': 'Agregar Medicamento',
    'no-medications-registered': 'No tienes medicamentos registrados',
    'start-adding-medication': 'Comienza agregando tu primer medicamento',
    'all-days': 'Todos los días',
    'no-specific-schedule': 'Sin horario específico',
    'schedules': 'Horarios',
    'days': 'Días',
    'low-stock': 'Stock bajo',
    'no-stock': 'Sin stock',
    'adjust': 'Ajustar',
    'edit': 'Editar',
    'delete': 'Eliminar',
    'confirm-deletion': 'Confirmar eliminación',
    'delete-medication-confirm': '¿Estás seguro de que quieres eliminar este medicamento? Esta acción no se puede deshacer.',
    'cancel': 'Cancelar',
    'update-stock': 'Actualizar Stock',
    'current-stock': 'Stock Actual',
    'max-stock': 'Stock Máximo',
    'reason-optional': 'Motivo (opcional)',
    'current-medication-amount': 'Cantidad actual de medicamento',
    'max-medication-amount': 'Cantidad máxima de medicamento',
    'restock-correction': 'Reabastecimiento, corrección, etc.',
    'update': 'Actualizar',
    // Days of the week
    'sunday': 'Domingo',
    'monday': 'Lunes',
    'tuesday': 'Martes',
    'wednesday': 'Miércoles',
    'thursday': 'Jueves',
    'friday': 'Viernes',
    'saturday': 'Sábado',
    // Form validation
    'medication-name-required': 'El nombre del medicamento es requerido',
    'dose-greater-than-zero': 'La dosis debe ser mayor a 0',
    'select-at-least-one-day': 'Selecciona al menos un día de la semana',
    'error-taking-medication': 'Error al tomar el medicamento',
    'error-undoing-action': 'Error al deshacer la acción',
    'error-adding-medication': 'Error al agregar medicamento',
    'edit-medication': 'Editar Medicamento',
    'medication-name-required-label': 'Nombre del Medicamento *',
    'dose-mg-required': 'Dosis (mg) *',
    'instructions': 'Instrucciones',
    'take-with-food-etc': 'Tomar con comida, antes de dormir, etc.',
    'low-stock-alert': 'Alerta de Stock Bajo',
    'taking-schedules': 'Horarios de Toma',
    'add-schedule': 'Agregar horario',
    'days-of-week-required': 'Días de la Semana *',
    'enable-reminders': 'Habilitar recordatorios',
    'sound-when-taking': 'Sonido al tomar',
    'additional-notes': 'Notas adicionales',
    'side-effects-reminders': 'Efectos secundarios, recordatorios especiales, etc.',
    'medication-form': 'Forma del Medicamento',
    'customize-form': 'Personalizar forma',
    'saving': 'Guardando...',
    'save': 'Guardar',
    'close': 'Cerrar',
    'medication-form-editor': 'Editor de Forma del Medicamento',
    // Pill shapes
    'shape-round': 'Redonda',
    'shape-oval': 'Ovalada',
    'shape-square': 'Cuadrada',
    'shape-rectangle': 'Rectangular',
    'shape-capsule': 'Cápsula',
    'shape-diamond': 'Diamante',
    'shape-hexagon': 'Hexágono',
    'shape-triangle': 'Triángulo',
    'shape-heart': 'Corazón',
    'shape-vaccine': 'Vacuna',
    'shape-serum': 'Suero',
    'shape-drop': 'Gota',
    'shape-bottle': 'Mamadera',
    'shape-powder': 'Polvo',
    'shape-vaper': 'Vaper',
    'shape-effervescent': 'Efervescente',
    'shape-ampoule': 'Ampolla',
    'shape-patch': 'Parche',
    'shape-spray': 'Spray',
    'shape-cream': 'Crema',
    'shape-nebulizer': 'Nebulizador',
    'shape-injection': 'Inyección',
    'shape-vitamin': 'Vitamina',
    // Pill patterns
    'pattern-none': 'Sin patrón',
    'pattern-line': 'Línea central',
    'pattern-cross': 'Cruz (X)',
    'pattern-dots': 'Puntos',
    'pattern-striped': 'Rayado',
    'pattern-plus': 'Más (+)',
    'pattern-groove': 'Ranura',
    'pattern-spiral': 'Espiral',
    'pattern-diamonds': 'Rombos',
    'pattern-waves': 'Ondas',
    // UI labels
    'forms': 'Formas',
    'size': 'Tamaño',
    'colors': 'Colores',
    'gradient': 'Gradiente',
    'patterns': 'Patrones',
    'thickness': 'Grosor',
    'direction': 'Dirección',
    'gradient-colors': 'Colores del Gradiente',
    'search-medication': 'Buscar medicamento...',
    'common-doses': 'Dosis comunes',
    'stocks': 'Stocks',
    'loading-stocks': 'Cargando stocks...',
    'no-stock-medications': 'No hay medicamentos con stock registrado',
    'medication': 'Medicamento',
    'stock': 'Stock',
    'state': 'Estado',
    'excellent': 'Excelente',
    'good': 'Bien',
    'low': 'Bajo',
    'critical': 'Crítico',
    'depleted': 'Agotado',
    'unknown': 'Desconocido',
    'todays-history': 'Historial de Hoy',
    'loading-history': 'Cargando historial...',
    'no-activity-today': 'No hay actividad registrada hoy',
    'medication-taken-at': 'Medicamento tomado a las',
    'stock-adjusted': 'Stock ajustado',
    'activity-recorded': 'Actividad registrada',
    'undo': 'Deshacer',
    'reminders-disabled': 'Recordatorios deshabilitados',
    'enable-notifications': 'Activa las notificaciones para ver tus recordatorios programados',
    'active-reminders': 'Recordatorios Activos',
    'upcoming-reminders': 'Próximos recordatorios programados para hoy',
    'no-pending-reminders': 'No hay recordatorios pendientes',
    'all-medications-up-to-date': 'Todos tus medicamentos están al día por hoy',
    'reminder': 'Recordatorio',
    'overdue': 'Vencido',
    'in': 'En',
    'min-before': 'min antes',
    'total-reminders-today': 'Total de recordatorios hoy',
    'active-of': 'activos de',
    // Home Screen translations
    'loading': 'Cargando...',
    'excellent-completion': '🎉 ¡Excelente! Has completado todas tus medicaciones de hoy',
    'great-progress': '🌟 ¡Muy bien! Estás muy cerca de completar el día',
    'good-progress': '💪 Buen progreso, ¡sigue así!',
    'good-start': '👍 Has comenzado el día bien, continúa',
    'time-to-start': '⏰ Es hora de comenzar con tus medicamentos',
    'daily-summary': 'Resumen de tus medicamentos de hoy',
    'todays-progress': 'Progreso de Hoy',
    'todays-medications': 'Medicamentos de Hoy',
    'medications-taken': 'medicamentos tomados',
    'no-specific-time': 'Sin horario específico',
    'medication-singular': 'medicamento',
    'medication-plural': 'medicamentos',
    'stock-units': 'Stock: {stock} unidades',
    'take-medication': 'Tomar',
    // Layout translations
    'current-case': 'Caso Actual:',
    'change-case': 'Cambiar Caso',
    'view-case-details': 'Ver detalles del caso',
    'no-case-permissions': 'ℹ️ No tienes permisos para ver detalles del caso',
    'no-permissions-case-details': 'No tienes permisos para ver detalles del caso',
    'create-join-case': '+ Crear o unirse a caso',
    // Stock update modal translations
    'stock-update-required': 'Actualización de Stock Requerida',
    'low-stock-warning': 'Advertencia de Stock Bajo',
    'stock-update-message': 'El medicamento "{medication}" tiene {currentStock} unidades restantes. ¿Te gustaría actualizar el stock antes de tomar este medicamento?',
    'new-stock-amount': 'Nueva Cantidad de Stock',
    'enter-new-stock': 'Ingresa la nueva cantidad de stock',
    'stock-update-hint': 'Esto actualizará el stock total para este medicamento',
    'take-without-updating': 'Tomar Sin Actualizar',
    'update-stock-and-take': 'Actualizar Stock y Tomar',
    // Case management translations
    'error-switching': 'Error al cambiar',
    'error-creating-case': 'Error al crear caso',
    'active-status': 'Activo',
    'my-medications': 'Mis Medicamentos',
    'medications-of': 'Medicamentos de {name}',
    'care-of': 'Cuidado de {name}',
    'create-new-case': 'Crear Nuevo Caso',
    'who-is-case-for': '¿Para quién es este caso?',
    'for-myself': 'Para mí mismo',
    'to-care-someone': 'Para cuidar a alguien',
    'for-my-pet': 'Para mi mascota',
    'continue': 'Continuar',
    'create-case': 'Crear Caso',
    'join-case': 'Unirse a un Caso',
    'invitation-code': 'Código de Invitación',
    'join-case-button': 'Unirse al Caso',
    'loading-cases': 'Cargando casos...',
    'collaborative-care': 'Sistema de Cuidado Colaborativo',
    'manage-with-family': 'Gestiona medicamentos con tu familia o equipo de cuidadores',
    'welcome-medication': '¡Bienvenido a tu Gestión de Medicamentos!',
    // Onboarding translations
    'welcome-app': '¡Bienvenido a Mi Medicación!',
    'lets-configure': 'vamos a configurar tu experiencia',
    'what-can-do': '¿Qué puedes hacer con Mi Medicación?',
    'manage-medications': '💊 Gestionar Medicamentos',
    'teamwork': '👥 Trabajo en Equipo',
    'reminders': '📱 Recordatorios',
    'start-setup': 'Comenzar Configuración',
    'how-use-app': '¿Cómo vas a usar Mi Medicación?',
    'i-am-caregiver': 'Soy un Cuidador',
    'manage-own-meds': 'Gestiono mis Propios Medicamentos',
    'join-existing-case': 'Unirme a un Caso Existente',
    'setup-care-case': 'Configurar Caso de Cuidado',
    'setup-personal-meds': 'Configurar tu Medicación Personal',
    'case-name': 'Nombre del Caso',
    'your-full-name': 'Tu nombre completo',
    'age-optional': 'Edad (opcional)',
    'description-optional': 'Descripción (opcional)',
    'validating': 'Validando...',
    // Authentication translations
    'register-new-account': 'Registra una nueva cuenta',
    'confirm-password': 'Confirmar Contraseña',
    'passwords-dont-match': 'Las contraseñas no coinciden',
    'or-register-with': 'O regístrate con',
    'continue-with-google': 'Continuar con Google',
    'back-to-login': 'Volver al inicio de sesión',
    'loading-invitation': 'Cargando invitación...',
    'you-have-been-invited': '¡Has sido invitado!',
    'accept-invitation': 'Aceptar Invitación',
    'go-to-dashboard': 'Ir al Dashboard',
    'invitation-to-join': 'Invitación para unirse',
    'login-to-accept': 'Inicia sesión para aceptar la invitación',
    'no-account': '¿No tienes cuenta?',
    'register-here': 'Regístrate aquí',
    'already-have-account': '¿Ya tienes cuenta?',
    'login-here': 'Inicia sesión'
  },
  en: {
    'app-title': 'RememMed',
    'login': 'Login',
    'register': 'Register',
    'username': 'Username',
    'password': 'Password',
    'email': 'Email',
    'loading': 'Loading...',
    'home': 'Home',
    'medications': 'Medications',
    'calendar': 'Calendar',
    'progress': 'Progress',
    'profile': 'Profile',
    'users': 'Current Case',
    'no-medications': 'No medications registered',
    'add-medication': 'Add Medication',
    'take-medication': 'Take Medication',
    'edit': 'Edit',
    'delete': 'Delete',
    'save': 'Save',
    'cancel': 'Cancel',
    'confirm': 'Confirm',
    'logout': 'Logout',
    'settings': 'Settings',
    'language': 'Language',
    'theme': 'Theme',
    'light': 'Light',
    'dark': 'Dark',
    'notifications': 'Notifications',
    'reminders': 'Reminders',
    'general': 'General',
    'account': 'Account',
    'privacy': 'Privacy',
    'about': 'About',
    'help': 'Help',
    'support': 'Support',
    'feedback': 'Feedback',
    'version': 'Version',
    'contact': 'Contact',
    'terms': 'Terms',
    'policy': 'Policy',
    'close': 'Close',
    'open': 'Open',
    'search': 'Search',
    'filter': 'Filter',
    'sort': 'Sort',
    'refresh': 'Refresh',
    'sync': 'Sync',
    'backup': 'Backup',
    'restore': 'Restore',
    'export': 'Export',
    'import': 'Import',
    'share': 'Share',
    'copy': 'Copy',
    'paste': 'Paste',
    'cut': 'Cut',
    'undo': 'Undo',
    'redo': 'Redo',
    'select-all': 'Select All',
    'deselect-all': 'Deselect All',
    'yes': 'Yes',
    'no': 'No',
    'ok': 'OK',
    'done': 'Done',
    'finish': 'Finish',
    'continue': 'Continue',
    'back': 'Back',
    'next': 'Next',
    'previous': 'Previous',
    'first': 'First',
    'last': 'Last',
    'today': 'Today',
    'yesterday': 'Yesterday',
    'tomorrow': 'Tomorrow',
    'week': 'Week',
    'month': 'Month',
    'year': 'Year',
    'daily': 'Daily',
    'weekly': 'Weekly',
    'monthly': 'Monthly',
    'yearly': 'Yearly',
    'name': 'Name',
    'description': 'Description',
    'date': 'Date',
    'time': 'Time',
    'status': 'Status',
    'active': 'Active',
    'inactive': 'Inactive',
    'enabled': 'Enabled',
    'disabled': 'Disabled',
    'required': 'Required',
    'optional': 'Optional',
    'available': 'Available',
    'unavailable': 'Unavailable',
    'online': 'Online',
    'offline': 'Offline',
    'connected': 'Connected',
    'disconnected': 'Disconnected',
    'success': 'Success',
    'error': 'Error',
    'warning': 'Warning',
    'info': 'Information',
    'emergency': 'Emergency',
    'urgent': 'Urgent',
    'normal': 'Normal',
    'low': 'Low',
    'medium': 'Medium',
    'high': 'High',
    'very-high': 'Very High',
    'all': 'All',
    'none': 'None',
    'other': 'Other',
    'unknown': 'Unknown',
    'new': 'New',
    'recent': 'Recent',
    'old': 'Old',
    'favorite': 'Favorite',
    'bookmark': 'Bookmark',
    'tag': 'Tag',
    'category': 'Category',
    'type': 'Type',
    'size': 'Size',
    'color': 'Color',
    'image': 'Image',
    'video': 'Video',
    'audio': 'Audio',
    'document': 'Document',
    'file': 'File',
    'folder': 'Folder',
    'link': 'Link',
    'url': 'URL',
    'website': 'Website',
    'page': 'Page',
    'section': 'Section',
    'item': 'Item',
    'list': 'List',
    'table': 'Table',
    'chart': 'Chart',
    'graph': 'Graph',
    'report': 'Report',
    'summary': 'Summary',
    'detail': 'Detail',
    'overview': 'Overview',
    'dashboard': 'Dashboard',
    'menu': 'Menu',
    'toolbar': 'Toolbar',
    'sidebar': 'Sidebar',
    'header': 'Header',
    'footer': 'Footer',
    'content': 'Content',
    'main': 'Main',
    'secondary': 'Secondary',
    'primary': 'Primary',
    'default': 'Default',
    'custom': 'Custom',
    'auto': 'Auto',
    'manual': 'Manual',
    'personal-info': 'Personal Information',
    'user-since': 'User since',
    'last-access': 'Last access',
    'member-since': 'Member since',
    'last-login': 'Last login',
    'change-avatar': 'Change avatar',
    'customize-avatar': 'Customize Avatar',
    'account-actions': 'Account Actions',
    'logout-description': 'This will log you out of your current session and redirect you to the login screen.',
    // Progress Screen translations
    'progress-overview': 'Progress Overview',
    'adherence-statistics': 'Adherence Statistics',
    'weekly-progress': 'Weekly Progress',
    'monthly-progress': 'Monthly Progress',
    'activity-history': 'Activity History',
    'current-streak': 'Current Streak',
    'best-streak': 'Best Streak',
    'perfect-days': 'Perfect Days',
    'total-taken': 'Total Taken',
    'average-adherence': 'Average Adherence',
    'total-scheduled': 'Total Scheduled',
    'medications-taken': 'Medications Taken',
    'medications-per-day': 'Medications per Day',
    'adherence-by-week': 'Adherence by Week',
    'no-activities': 'No activities to show',
    'no-medications-date': 'No medications recorded on this date',
    'start-tracking': 'Start taking your medications to see your progress here',
    'advanced-statistics': 'Advanced Statistics',
    'last-week': 'Last week',
    'last-month': 'Last month',
    'last-3-months': 'Last 3 months',
    'best-streak-days': 'Best streak (days)',
    'current-streak-days': 'Current streak',
    'daily-average': 'Daily average',
    'time-patterns': 'Time Patterns',
    'recent-trend': 'Recent Trend (7 days)',
    'insights-data': 'Insights from your Data',
    'current-level': 'Level',
    'medication-master': 'Medication Master',
    'total-points': 'Total Points',
    'next-level': 'Next level',
    'general-tab': 'General',
    'challenges-tab': 'Challenges',
    'analytics-tab': 'Analytics',
    'active-challenge': 'Active Challenge',
    'detailed-weekly': 'Detailed Weekly Analysis',
    'performance-summary': 'Performance Summary',
    'total-medications': 'Total Medications',
    'general-adherence': 'General Adherence',
    'most-active-hour': 'Most Active Hour',
    'filter-all': 'All',
    'filter-taken': 'Taken',
    'filter-missed': 'Missed',
    'filter-stock': 'Stock adjusted',
    'group-by-date': 'Group by date',
    'list-view': 'List view',
    'previous-month': 'Previous month',
    'next-month': 'Next month',
    'current-month': 'Go to current month',
    'month-label': 'Month',
    'year-label': 'Year',
    'filter-label': 'Filter',
    'taken-status': 'Taken',
    'missed-status': 'Missed',
    'skipped-status': 'Skipped',
    'stock-adjusted-status': 'Stock adjusted',
    'scheduled-time': 'Scheduled',
    'unknown-medication': 'Unknown medication',
    'trend-improving': 'Your adherence is improving over time',
    'trend-declining': 'Your adherence has declined. Stay focused!',
    'trend-stable': 'Your adherence remains stable',
    'perfect-week': 'Perfect Week',
    'perfect-week-desc': 'Achieve 100% adherence this week',
    'daily-consistency': 'Daily Consistency',
    'daily-consistency-desc': 'Don\'t miss any medication today',
    'weekly-reward': '"Perfect Week" Badge + 500 points',
    'daily-reward': '100 bonus points',
    'days-remaining': 'days remaining',
    'until-midnight': 'Until midnight',
    'reward-label': 'Reward',
    'progress-label': 'Progress',
    'personalized-insights': 'Personalized Insights',
    'tip-label': 'Tip',
    'tip-message': 'Once you register taken medications, they will appear here to help you track your progress',
    'no-case-selected': 'No case selected',
    'select-case-progress': 'Select a case to view progress',
    'achievements': 'Achievements',
    'history': 'History',
    'stats-achievements': 'Statistics and achievements for',
    'general-statistics': 'General Statistics',
    'level-experience': 'Level and Experience',
    'recent-adherence': 'Recent Adherence',
    'adherence': 'Adherence',
    'hot-streak': 'Hot streak!',
    'consecutive-days': 'consecutive days',
    'level-achieved': 'Level',
    'true-expert': 'You are a true expert',
    'royal-adherence': 'Royal adherence!',
    'top-1-percent': 'You are in the top 1%',
    'perfect-days-achieved': 'perfect days achieved',
    'golden-hour': 'Your golden hour',
    // Medications Screen translations
    'manage-medications': 'Manage your medications and schedules',
    'add-medication': 'Add Medication',
    'no-medications-registered': 'You have no medications registered',
    'start-adding-medication': 'Start by adding your first medication',
    'all-days': 'All days',
    'no-specific-schedule': 'No specific schedule',
    'schedules': 'Schedules',
    'days': 'Days',
    'low-stock': 'Low stock',
    'no-stock': 'No stock',
    'adjust': 'Adjust',
    'edit': 'Edit',
    'delete': 'Delete',
    'confirm-deletion': 'Confirm deletion',
    'delete-medication-confirm': 'Are you sure you want to delete this medication? This action cannot be undone.',
    'cancel': 'Cancel',
    'update-stock': 'Update Stock',
    'current-stock': 'Current Stock',
    'max-stock': 'Max Stock',
    'reason-optional': 'Reason (optional)',
    'current-medication-amount': 'Current medication amount',
    'max-medication-amount': 'Maximum medication amount',
    'restock-correction': 'Restock, correction, etc.',
    'update': 'Update',
    // Days of the week
    'sunday': 'Sunday',
    'monday': 'Monday',
    'tuesday': 'Tuesday',
    'wednesday': 'Wednesday',
    'thursday': 'Thursday',
    'friday': 'Friday',
    'saturday': 'Saturday',
    // Form validation
    'medication-name-required': 'Medication name is required',
    'dose-greater-than-zero': 'Dose must be greater than 0',
    'select-at-least-one-day': 'Select at least one day of the week',
    'error-taking-medication': 'Error taking medication',
    'error-undoing-action': 'Error undoing action',
    'error-adding-medication': 'Error adding medication',
    'edit-medication': 'Edit Medication',
    'medication-name-required-label': 'Medication Name *',
    'dose-mg-required': 'Dose (mg) *',
    'instructions': 'Instructions',
    'take-with-food-etc': 'Take with food, before bedtime, etc.',
    'low-stock-alert': 'Low Stock Alert',
    'taking-schedules': 'Taking Schedules',
    'add-schedule': 'Add schedule',
    'days-of-week-required': 'Days of the Week *',
    'enable-reminders': 'Enable reminders',
    'sound-when-taking': 'Sound when taking',
    'additional-notes': 'Additional notes',
    'side-effects-reminders': 'Side effects, special reminders, etc.',
    'medication-form': 'Medication Form',
    'customize-form': 'Customize form',
    'saving': 'Saving...',
    'save': 'Save',
    'close': 'Close',
    'medication-form-editor': 'Medication Form Editor',
    // Pill shapes
    'shape-round': 'Round',
    'shape-oval': 'Oval',
    'shape-square': 'Square',
    'shape-rectangle': 'Rectangle',
    'shape-capsule': 'Capsule',
    'shape-diamond': 'Diamond',
    'shape-hexagon': 'Hexagon',
    'shape-triangle': 'Triangle',
    'shape-heart': 'Heart',
    'shape-vaccine': 'Vaccine',
    'shape-serum': 'Serum',
    'shape-drop': 'Drop',
    'shape-bottle': 'Bottle',
    'shape-powder': 'Powder',
    'shape-vaper': 'Vaper',
    'shape-effervescent': 'Effervescent',
    'shape-ampoule': 'Ampoule',
    'shape-patch': 'Patch',
    'shape-spray': 'Spray',
    'shape-cream': 'Cream',
    'shape-nebulizer': 'Nebulizer',
    'shape-injection': 'Injection',
    'shape-vitamin': 'Vitamin',
    // Pill patterns
    'pattern-none': 'No pattern',
    'pattern-line': 'Center line',
    'pattern-cross': 'Cross (X)',
    'pattern-dots': 'Dots',
    'pattern-striped': 'Striped',
    'pattern-plus': 'Plus (+)',
    'pattern-groove': 'Groove',
    'pattern-spiral': 'Spiral',
    'pattern-diamonds': 'Diamonds',
    'pattern-waves': 'Waves',
    // UI labels
    'forms': 'Forms',
    'size': 'Size',
    'colors': 'Colors',
    'gradient': 'Gradient',
    'patterns': 'Patterns',
    'thickness': 'Thickness',
    'direction': 'Direction',
    'gradient-colors': 'Gradient Colors',
    'search-medication': 'Search medication...',
    'common-doses': 'Common doses',
    'stocks': 'Stocks',
    'loading-stocks': 'Loading stocks...',
    'no-stock-medications': 'No medications with registered stock',
    'medication': 'Medication',
    'stock': 'Stock',
    'state': 'State',
    'excellent': 'Excellent',
    'good': 'Good',
    'low': 'Low',
    'critical': 'Critical',
    'depleted': 'Depleted',
    'unknown': 'Unknown',
    'todays-history': 'Today\'s History',
    'loading-history': 'Loading history...',
    'no-activity-today': 'No activity recorded today',
    'medication-taken-at': 'Medication taken at',
    'stock-adjusted': 'Stock adjusted',
    'activity-recorded': 'Activity recorded',
    'undo': 'Undo',
    'reminders-disabled': 'Reminders disabled',
    'enable-notifications': 'Enable notifications to see your scheduled reminders',
    'active-reminders': 'Active Reminders',
    'upcoming-reminders': 'Upcoming reminders scheduled for today',
    'no-pending-reminders': 'No pending reminders',
    'all-medications-up-to-date': 'All your medications are up to date for today',
    'reminder': 'Reminder',
    'overdue': 'Overdue',
    'in': 'In',
    'min-before': 'min before',
    'total-reminders-today': 'Total reminders today',
    'active-of': 'active of',
    // Home Screen translations
    'loading': 'Loading...',
    'excellent-completion': '🎉 Excellent! You have completed all your medications today',
    'great-progress': '🌟 Great! You are very close to completing the day',
    'good-progress': '💪 Good progress, keep it up!',
    'good-start': '👍 You have started the day well, continue',
    'time-to-start': '⏰ It\'s time to start with your medications',
    'daily-summary': 'Summary of your medications today',
    'todays-progress': 'Today\'s Progress',
    'todays-medications': 'Today\'s Medications',
    'medications-taken': 'medications taken',
    'no-specific-time': 'No specific time',
    'medication-singular': 'medication',
    'medication-plural': 'medications',
    // MainLayout translations
    'current-case': 'Current Case:',
    'change-case': 'Change Case',
    'no-name': 'No name',
    'view-case-details': 'View case details',
    'no-permissions-case-details': 'You don\'t have permissions to view case details',
    'create-join-case': '+ Create or join case',
    // Stock update modal translations
    'stock-update-required': 'Stock Update Required',
    'low-stock-warning': 'Low Stock Warning',
    'stock-update-message': 'The medication "{medication}" has {currentStock} units remaining. Would you like to update the stock before taking this medication?',
    'new-stock-amount': 'New Stock Amount',
    'enter-new-stock': 'Enter new stock amount',
    'stock-update-hint': 'This will update the total stock for this medication',
    'take-without-updating': 'Take Without Updating',
    'update-stock-and-take': 'Update Stock & Take',
    // Case management translations
    'error-switching': 'Error switching',
    'error-creating-case': 'Error creating case',
    'active-status': 'Active',
    'my-medications': 'My Medications',
    'medications-of': 'Medications of {name}',
    'care-of': 'Care of {name}',
    'create-new-case': 'Create New Case',
    'who-is-case-for': 'Who is this case for?',
    'for-myself': 'For myself',
    'to-care-someone': 'To care for someone',
    'for-my-pet': 'For my pet',
    'continue': 'Continue',
    'create-case': 'Create Case',
    'join-case': 'Join a Case',
    'invitation-code': 'Invitation Code',
    'join-case-button': 'Join Case',
    'loading-cases': 'Loading cases...',
    'collaborative-care': 'Collaborative Care System',
    'manage-with-family': 'Manage medications with your family or care team',
    'welcome-medication': 'Welcome to your Medication Management!',
    // Onboarding translations
    'welcome-app': 'Welcome to My Medication!',
    'lets-configure': 'let\'s configure your experience',
    'what-can-do': 'What can you do with My Medication?',
    'manage-medications': '💊 Manage Medications',
    'teamwork': '👥 Teamwork',
    'reminders': '📱 Reminders',
    'start-setup': 'Start Setup',
    'how-use-app': 'How will you use My Medication?',
    'i-am-caregiver': 'I am a Caregiver',
    'manage-own-meds': 'I Manage my Own Medications',
    'join-existing-case': 'Join an Existing Case',
    'setup-care-case': 'Setup Care Case',
    'setup-personal-meds': 'Setup your Personal Medication',
    'case-name': 'Case Name',
    'your-full-name': 'Your full name',
    'age-optional': 'Age (optional)',
    'description-optional': 'Description (optional)',
    'validating': 'Validating...',
    // Authentication translations
    'register-new-account': 'Register a new account',
    'confirm-password': 'Confirm Password',
    'passwords-dont-match': 'Passwords don\'t match',
    'or-register-with': 'Or register with',
    'continue-with-google': 'Continue with Google',
    'back-to-login': 'Back to login',
    'loading-invitation': 'Loading invitation...',
    'you-have-been-invited': 'You have been invited!',
    'accept-invitation': 'Accept Invitation',
    'go-to-dashboard': 'Go to Dashboard',
    'invitation-to-join': 'Invitation to join',
    'login-to-accept': 'Login to accept the invitation',
    'no-account': 'Don\'t have an account?',
    'register-here': 'Register here',
    'already-have-account': 'Already have an account?',
    'login-here': 'Login here'
  },
  pt: {
    'app-title': 'Minha Medicação',
    'login': 'Entrar',
    'register': 'Criar Conta',
    'username': 'Usuário',
    'password': 'Senha',
    'email': 'E-mail',
    'loading': 'Carregando...',
    'home': 'Início',
    'medications': 'Medicamentos',
    'calendar': 'Calendário',
    'progress': 'Progresso',
    'profile': 'Perfil',
    'users': 'Caso Atual',
    'no-medications': 'Nenhum medicamento registrado',
    'add-medication': 'Adicionar Medicamento',
    'take-medication': 'Tomar Medicamento',
    'edit': 'Editar',
    'delete': 'Excluir',
    'save': 'Salvar',
    'cancel': 'Cancelar',
    'confirm': 'Confirmar',
    'logout': 'Sair',
    'settings': 'Configurações',
    'language': 'Idioma',
    'theme': 'Tema',
    'light': 'Claro',
    'dark': 'Escuro',
    'notifications': 'Notificações',
    'reminders': 'Lembretes',
    'general': 'Geral',
    'account': 'Conta',
    'privacy': 'Privacidade',
    'about': 'Sobre',
    'help': 'Ajuda',
    'support': 'Suporte',
    'feedback': 'Comentários',
    'version': 'Versão',
    'contact': 'Contato',
    'terms': 'Termos',
    'policy': 'Política',
    'close': 'Fechar',
    'open': 'Abrir',
    'search': 'Pesquisar',
    'filter': 'Filtrar',
    'sort': 'Ordenar',
    'refresh': 'Atualizar',
    'sync': 'Sincronizar',
    'backup': 'Backup',
    'restore': 'Restaurar',
    'export': 'Exportar',
    'import': 'Importar',
    'share': 'Compartilhar',
    'copy': 'Copiar',
    'paste': 'Colar',
    'cut': 'Cortar',
    'undo': 'Desfazer',
    'redo': 'Refazer',
    'select-all': 'Selecionar tudo',
    'deselect-all': 'Desmarcar tudo',
    'yes': 'Sim',
    'no': 'Não',
    'ok': 'OK',
    'done': 'Pronto',
    'finish': 'Finalizar',
    'continue': 'Continuar',
    'back': 'Voltar',
    'next': 'Próximo',
    'previous': 'Anterior',
    'first': 'Primeiro',
    'last': 'Último',
    'today': 'Hoje',
    'yesterday': 'Ontem',
    'tomorrow': 'Amanhã',
    'week': 'Semana',
    'month': 'Mês',
    'year': 'Ano',
    'daily': 'Diário',
    'weekly': 'Semanal',
    'monthly': 'Mensal',
    'yearly': 'Anual',
    'name': 'Nome',
    'description': 'Descrição',
    'date': 'Data',
    'time': 'Hora',
    'status': 'Status',
    'active': 'Ativo',
    'inactive': 'Inativo',
    'enabled': 'Habilitado',
    'disabled': 'Desabilitado',
    'required': 'Obrigatório',
    'optional': 'Opcional',
    'available': 'Disponível',
    'unavailable': 'Indisponível',
    'online': 'Online',
    'offline': 'Offline',
    'connected': 'Conectado',
    'disconnected': 'Desconectado',
    'success': 'Sucesso',
    'error': 'Erro',
    'warning': 'Aviso',
    'info': 'Informação',
    'emergency': 'Emergência',
    'urgent': 'Urgente',
    'normal': 'Normal',
    'low': 'Baixo',
    'medium': 'Médio',
    'high': 'Alto',
    'very-high': 'Muito alto',
    'all': 'Todos',
    'none': 'Nenhum',
    'other': 'Outro',
    'unknown': 'Desconhecido',
    'new': 'Novo',
    'recent': 'Recente',
    'old': 'Antigo',
    'favorite': 'Favorito',
    'bookmark': 'Marcador',
    'tag': 'Tag',
    'category': 'Categoria',
    'type': 'Tipo',
    'size': 'Tamanho',
    'color': 'Cor',
    'image': 'Imagem',
    'video': 'Vídeo',
    'audio': 'Áudio',
    'document': 'Documento',
    'file': 'Arquivo',
    'folder': 'Pasta',
    'link': 'Link',
    'url': 'URL',
    'website': 'Site',
    'page': 'Página',
    'section': 'Seção',
    'item': 'Item',
    'list': 'Lista',
    'table': 'Tabela',
    'chart': 'Gráfico',
    'graph': 'Gráfico',
    'report': 'Relatório',
    'summary': 'Resumo',
    'detail': 'Detalhe',
    'overview': 'Visão geral',
    'dashboard': 'Painel',
    'menu': 'Menu',
    'toolbar': 'Barra de ferramentas',
    'sidebar': 'Barra lateral',
    'header': 'Cabeçalho',
    'footer': 'Rodapé',
    'content': 'Conteúdo',
    'main': 'Principal',
    'secondary': 'Secundário',
    'primary': 'Primário',
    'default': 'Padrão',
    'custom': 'Personalizado',
    'auto': 'Automático',
    'manual': 'Manual',
    'personal-info': 'Informações Pessoais',
    'user-since': 'Usuário desde',
    'last-access': 'Último acesso',
    'member-since': 'Membro desde',
    'last-login': 'Último login',
    'change-avatar': 'Alterar avatar',
    'customize-avatar': 'Personalizar Avatar',
    'account-actions': 'Ações da Conta',
    'logout-description': 'Isso irá desconectá-lo da sua sessão atual e redirecioná-lo para a tela de login.',
    // Progress Screen translations
    'progress-overview': 'Visão Geral do Progresso',
    'adherence-statistics': 'Estatísticas de Aderência',
    'weekly-progress': 'Progresso Semanal',
    'monthly-progress': 'Progresso Mensal',
    'activity-history': 'Histórico de Atividades',
    'current-streak': 'Sequência Atual',
    'best-streak': 'Melhor Sequência',
    'perfect-days': 'Dias Perfeitos',
    'total-taken': 'Total Tomados',
    'average-adherence': 'Aderência Média',
    'total-scheduled': 'Total Programados',
    'medications-taken': 'Medicamentos Tomados',
    'medications-per-day': 'Medicamentos por Dia',
    'adherence-by-week': 'Aderência por Semana',
    'no-activities': 'Nenhuma atividade para mostrar',
    'no-medications-date': 'Nenhum medicamento registrado nesta data',
    'start-tracking': 'Comece a tomar seus medicamentos para ver seu progresso aqui',
    'advanced-statistics': 'Estatísticas Avançadas',
    'last-week': 'Última semana',
    'last-month': 'Último mês',
    'last-3-months': 'Últimos 3 meses',
    'best-streak-days': 'Melhor sequência (dias)',
    'current-streak-days': 'Sequência atual',
    'daily-average': 'Média diária',
    'time-patterns': 'Padrões de Horário',
    'recent-trend': 'Tendência Recente (7 dias)',
    'insights-data': 'Insights dos seus Dados',
    'current-level': 'Nível',
    'medication-master': 'Mestre dos Medicamentos',
    'total-points': 'Pontos Totais',
    'next-level': 'Próximo nível',
    'general-tab': 'Geral',
    'challenges-tab': 'Desafios',
    'analytics-tab': 'Análise',
    'active-challenge': 'Desafio Ativo',
    'detailed-weekly': 'Análise Semanal Detalhada',
    'performance-summary': 'Resumo de Desempenho',
    'total-medications': 'Total de Medicamentos',
    'general-adherence': 'Aderência Geral',
    'most-active-hour': 'Hora Mais Ativa',
    'filter-all': 'Todos',
    'filter-taken': 'Tomados',
    'filter-missed': 'Perdidos',
    'filter-stock': 'Estoque ajustado',
    'group-by-date': 'Agrupar por data',
    'list-view': 'Visualização em lista',
    'previous-month': 'Mês anterior',
    'next-month': 'Próximo mês',
    'current-month': 'Ir para o mês atual',
    'month-label': 'Mês',
    'year-label': 'Ano',
    'filter-label': 'Filtro',
    'taken-status': 'Tomado',
    'missed-status': 'Perdido',
    'skipped-status': 'Pulado',
    'stock-adjusted-status': 'Estoque ajustado',
    'scheduled-time': 'Programado',
    'unknown-medication': 'Medicamento desconhecido',
    'trend-improving': 'Sua aderência está melhorando com o tempo',
    'trend-declining': 'Sua aderência diminuiu. Mantenha-se focado!',
    'trend-stable': 'Sua aderência permanece estável',
    'perfect-week': 'Semana Perfeita',
    'perfect-week-desc': 'Alcance 100% de aderência esta semana',
    'daily-consistency': 'Consistência Diária',
    'daily-consistency-desc': 'Não perca nenhum medicamento hoje',
    'weekly-reward': 'Badge "Semana Perfeita" + 500 pontos',
    'daily-reward': '100 pontos de bônus',
    'days-remaining': 'dias restantes',
    'until-midnight': 'Até meia-noite',
    'reward-label': 'Recompensa',
    'progress-label': 'Progresso',
    'personalized-insights': 'Insights Personalizados',
    'tip-label': 'Dica',
    'tip-message': 'Uma vez que você registre medicamentos tomados, eles aparecerão aqui para ajudá-lo a acompanhar seu progresso',
    'no-case-selected': 'Nenhum caso selecionado',
    'select-case-progress': 'Selecione um caso para ver o progresso',
    'achievements': 'Conquistas',
    'history': 'Histórico',
    'stats-achievements': 'Estatísticas e conquistas para',
    'general-statistics': 'Estatísticas Gerais',
    'level-experience': 'Nível e Experiência',
    'recent-adherence': 'Aderência Recente',
    'adherence': 'Aderência',
    'hot-streak': 'Sequência quente!',
    'consecutive-days': 'dias consecutivos',
    'level-achieved': 'Nível',
    'true-expert': 'Você é um verdadeiro especialista',
    'royal-adherence': 'Aderência real!',
    'top-1-percent': 'Você está no top 1%',
    'perfect-days-achieved': 'dias perfeitos alcançados',
    'golden-hour': 'Sua hora dourada',
    // Medications Screen translations
    'manage-medications': 'Gerencie seus medicamentos e horários',
    'add-medication': 'Adicionar Medicamento',
    'no-medications-registered': 'Você não tem medicamentos registrados',
    'start-adding-medication': 'Comece adicionando seu primeiro medicamento',
    'all-days': 'Todos os dias',
    'no-specific-schedule': 'Sem horário específico',
    'schedules': 'Horários',
    'days': 'Dias',
    'low-stock': 'Estoque baixo',
    'no-stock': 'Sem estoque',
    'adjust': 'Ajustar',
    'edit': 'Editar',
    'delete': 'Excluir',
    'confirm-deletion': 'Confirmar exclusão',
    'delete-medication-confirm': 'Tem certeza de que deseja excluir este medicamento? Esta ação não pode ser desfeita.',
    'cancel': 'Cancelar',
    'update-stock': 'Atualizar Estoque',
    'current-stock': 'Estoque Atual',
    'max-stock': 'Estoque Máximo',
    'reason-optional': 'Motivo (opcional)',
    'current-medication-amount': 'Quantidade atual de medicamento',
    'max-medication-amount': 'Quantidade máxima de medicamento',
    'restock-correction': 'Reabastecimento, correção, etc.',
    'update': 'Atualizar',
    // Days of the week
    'sunday': 'Domingo',
    'monday': 'Segunda',
    'tuesday': 'Terça',
    'wednesday': 'Quarta',
    'thursday': 'Quinta',
    'friday': 'Sexta',
    'saturday': 'Sábado',
    // Form validation
    'medication-name-required': 'Nome do medicamento é obrigatório',
    'dose-greater-than-zero': 'Dose deve ser maior que 0',
    'select-at-least-one-day': 'Selecione pelo menos um dia da semana',
    'error-adding-medication': 'Erro ao adicionar medicamento',
    'edit-medication': 'Editar Medicamento',
    'medication-name-required-label': 'Nome do Medicamento *',
    'dose-mg-required': 'Dose (mg) *',
    'instructions': 'Instruções',
    'take-with-food-etc': 'Tomar com comida, antes de dormir, etc.',
    'low-stock-alert': 'Alerta de Estoque Baixo',
    'taking-schedules': 'Horários de Tomada',
    'add-schedule': 'Adicionar horário',
    'days-of-week-required': 'Dias da Semana *',
    'enable-reminders': 'Habilitar lembretes',
    'sound-when-taking': 'Som ao tomar',
    'additional-notes': 'Notas adicionais',
    'side-effects-reminders': 'Efeitos colaterais, lembretes especiais, etc.',
    'medication-form': 'Forma do Medicamento',
    'customize-form': 'Personalizar forma',
    'saving': 'Salvando...',
    'save': 'Salvar',
    'close': 'Fechar',
    'medication-form-editor': 'Editor de Forma do Medicamento',
    // Pill shapes
    'shape-round': 'Redonda',
    'shape-oval': 'Oval',
    'shape-square': 'Quadrada',
    'shape-rectangle': 'Retangular',
    'shape-capsule': 'Cápsula',
    'shape-diamond': 'Diamante',
    'shape-hexagon': 'Hexágono',
    'shape-triangle': 'Triângulo',
    'shape-heart': 'Coração',
    'shape-vaccine': 'Vacina',
    'shape-serum': 'Soro',
    'shape-drop': 'Gota',
    'shape-bottle': 'Mamadeira',
    'shape-powder': 'Pó',
    'shape-vaper': 'Vaper',
    'shape-effervescent': 'Efervescente',
    'shape-ampoule': 'Ampola',
    'shape-patch': 'Adesivo',
    'shape-spray': 'Spray',
    'shape-cream': 'Creme',
    'shape-nebulizer': 'Nebulizador',
    'shape-injection': 'Injeção',
    'shape-vitamin': 'Vitamina',
    // Pill patterns
    'pattern-none': 'Sem padrão',
    'pattern-line': 'Linha central',
    'pattern-cross': 'Cruz (X)',
    'pattern-dots': 'Pontos',
    'pattern-striped': 'Listrado',
    'pattern-plus': 'Mais (+)',
    'pattern-groove': 'Sulco',
    'pattern-spiral': 'Espiral',
    'pattern-diamonds': 'Losangos',
    'pattern-waves': 'Ondas',
    // UI labels
    'forms': 'Formas',
    'size': 'Tamanho',
    'colors': 'Cores',
    'gradient': 'Gradiente',
    'patterns': 'Padrões',
    'thickness': 'Espessura',
    'direction': 'Direção',
    'gradient-colors': 'Cores do Gradiente',
    'search-medication': 'Pesquisar medicamento...',
    'common-doses': 'Doses comuns',
    'stocks': 'Estoques',
    'loading-stocks': 'Carregando estoques...',
    'no-stock-medications': 'Nenhum medicamento com estoque registrado',
    'medication': 'Medicamento',
    'stock': 'Estoque',
    'state': 'Estado',
    'excellent': 'Excelente',
    'good': 'Bom',
    'low': 'Baixo',
    'critical': 'Crítico',
    'depleted': 'Esgotado',
    'unknown': 'Desconhecido',
    'todays-history': 'Histórico de Hoje',
    'loading-history': 'Carregando histórico...',
    'no-activity-today': 'Nenhuma atividade registrada hoje',
    'medication-taken-at': 'Medicamento tomado às',
    'stock-adjusted': 'Estoque ajustado',
    'activity-recorded': 'Atividade registrada',
    'undo': 'Desfazer',
    'reminders-disabled': 'Lembretes desabilitados',
    'enable-notifications': 'Ative as notificações para ver seus lembretes programados',
    'active-reminders': 'Lembretes Ativos',
    'upcoming-reminders': 'Próximos lembretes programados para hoje',
    'no-pending-reminders': 'Nenhum lembrete pendente',
    'all-medications-up-to-date': 'Todos os seus medicamentos estão em dia para hoje',
    'reminder': 'Lembrete',
    'overdue': 'Vencido',
    'in': 'Em',
    'min-before': 'min antes',
    'total-reminders-today': 'Total de lembretes hoje',
    'active-of': 'ativos de',
    // Home Screen translations
    'loading': 'Carregando...',
    'excellent-completion': '🎉 Excelente! Você completou todas as suas medicações hoje',
    'great-progress': '🌟 Muito bem! Você está muito perto de completar o dia',
    'good-progress': '💪 Bom progresso, continue assim!',
    'good-start': '👍 Você começou o dia bem, continue',
    'time-to-start': '⏰ É hora de começar com seus medicamentos',
    'daily-summary': 'Resumo dos seus medicamentos hoje',
    'todays-progress': 'Progresso de Hoje',
    'todays-medications': 'Medicamentos de Hoje',
    'medications-taken': 'medicamentos tomados',
    'no-specific-time': 'Sem horário específico',
    'medication-singular': 'medicamento',
    'medication-plural': 'medicamentos',
    'stock-units': 'Estoque: {stock} unidades',
    'take-medication': 'Tomar',
    // Layout translations
    'current-case': 'Caso Atual:',
    'change-case': 'Alterar Caso',
    'view-case-details': 'Ver detalhes do caso',
    'no-case-permissions': 'ℹ️ Você não tem permissões para ver detalhes do caso',
    'no-permissions-case-details': 'Você não tem permissões para ver detalhes do caso',
    'create-join-case': '+ Criar ou participar de caso',
    // Stock update modal translations
    'stock-update-required': 'Atualização de Estoque Necessária',
    'low-stock-warning': 'Aviso de Estoque Baixo',
    'stock-update-message': 'O medicamento "{medication}" tem {currentStock} unidades restantes. Gostaria de atualizar o estoque antes de tomar este medicamento?',
    'new-stock-amount': 'Nova Quantidade de Estoque',
    'enter-new-stock': 'Digite a nova quantidade de estoque',
    'stock-update-hint': 'Isso atualizará o estoque total para este medicamento',
    'take-without-updating': 'Tomar Sem Atualizar',
    'update-stock-and-take': 'Atualizar Estoque e Tomar',
    // Case management translations
    'error-switching': 'Erro ao alterar',
    'error-creating-case': 'Erro ao criar caso',
    'active-status': 'Ativo',
    'my-medications': 'Meus Medicamentos',
    'medications-of': 'Medicamentos de {name}',
    'care-of': 'Cuidado de {name}',
    'create-new-case': 'Criar Novo Caso',
    'who-is-case-for': 'Para quem é este caso?',
    'for-myself': 'Para mim mesmo',
    'to-care-someone': 'Para cuidar de alguém',
    'for-my-pet': 'Para meu animal de estimação',
    'continue': 'Continuar',
    'create-case': 'Criar Caso',
    'join-case': 'Participar de um Caso',
    'invitation-code': 'Código de Convite',
    'join-case-button': 'Participar do Caso',
    'loading-cases': 'Carregando casos...',
    'collaborative-care': 'Sistema de Cuidado Colaborativo',
    'manage-with-family': 'Gerencie medicamentos com sua família ou equipe de cuidadores',
    'welcome-medication': 'Bem-vindo ao seu Gerenciamento de Medicamentos!',
    // Onboarding translations
    'welcome-app': 'Bem-vindo ao Minha Medicação!',
    'lets-configure': 'vamos configurar sua experiência',
    'what-can-do': 'O que você pode fazer com Minha Medicação?',
    'manage-medications': '💊 Gerenciar Medicamentos',
    'teamwork': '👥 Trabalho em Equipe',
    'reminders': '📱 Lembretes',
    'start-setup': 'Iniciar Configuração',
    'how-use-app': 'Como você vai usar Minha Medicação?',
    'i-am-caregiver': 'Eu sou um Cuidador',
    'manage-own-meds': 'Eu Gerencio meus Próprios Medicamentos',
    'join-existing-case': 'Participar de um Caso Existente',
    'setup-care-case': 'Configurar Caso de Cuidado',
    'setup-personal-meds': 'Configurar sua Medicação Pessoal',
    'case-name': 'Nome do Caso',
    'your-full-name': 'Seu nome completo',
    'age-optional': 'Idade (opcional)',
    'description-optional': 'Descrição (opcional)',
    'validating': 'Validando...',
    // Authentication translations
    'register-new-account': 'Registrar uma nova conta',
    'confirm-password': 'Confirmar Senha',
    'passwords-dont-match': 'As senhas não coincidem',
    'or-register-with': 'Ou registre-se com',
    'continue-with-google': 'Continuar com Google',
    'back-to-login': 'Voltar ao login',
    'loading-invitation': 'Carregando convite...',
    'you-have-been-invited': 'Você foi convidado!',
    'accept-invitation': 'Aceitar Convite',
    'go-to-dashboard': 'Ir para o Dashboard',
    'invitation-to-join': 'Convite para participar',
    'login-to-accept': 'Faça login para aceitar o convite',
    'no-account': 'Não tem uma conta?',
    'register-here': 'Registre-se aqui',
    'already-have-account': 'Já tem uma conta?',
    'login-here': 'Faça login aqui'
  }
}

interface I18nProviderProps {
  children: React.ReactNode
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('language') || 'es'
  })

  const setLanguage = useCallback((language: string) => {
    setCurrentLanguage(language)
    localStorage.setItem('language', language)
    document.documentElement.lang = language
  }, [])

  const t = useCallback((key: string, variables?: Record<string, string | number>): string => {
    const translation = translations[currentLanguage as keyof typeof translations]
    let result = translation[key as keyof typeof translation] || key

    // Handle variable interpolation
    if (variables) {
      Object.entries(variables).forEach(([variable, value]) => {
        result = result.replace(`{${variable}}`, String(value))
      })
    }

    return result
  }, [currentLanguage])

  const updateUI = useCallback(() => {
    // This function can be used to trigger UI updates when language changes
    document.documentElement.lang = currentLanguage
  }, [currentLanguage])

  const value = {
    currentLanguage,
    setLanguage,
    t,
    updateUI
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
} 